import { getRecents, recordRecent } from './global-search.js';
import { deriveMode, statusText, activeScopeText } from './search-dropdown-model.js';

const $ = (id) => document.getElementById(id);
const element = (tag, className, text) => {
  const el = document.createElement(tag);
  el.className = className;
  if (text) el.textContent = text;
  return el;
};

/** Games-only search keeps its request owner until both fetch and JSON settle. */
export function createGameChangeSearch({ current, scopes, makeRow, resetScopes, systemAvailable }) {
  let generation = 0;
  let timer;
  let controller;
  const cancel = () => {
    generation += 1;
    clearTimeout(timer);
    controller?.abort();
    controller = null;
    $('gc-count')?.removeAttribute('aria-label');
  };
  const count = (text, label) => {
    const field = $('gc-count');
    if (!field) return;
    field.textContent = text;
    if (label) field.setAttribute('aria-label', label);
    else field.removeAttribute('aria-label');
  };
  const group = (list, label, rows) => {
    if (!rows.length) return;
    const wrap = element('div', 'gc-group');
    if (label) wrap.append(element('p', 'gc-group-label', label));
    const body = element('div', 'gc-group-rows');
    body.append(...rows);
    wrap.append(body);
    list.append(wrap);
  };
  const gameRow = (game, query = '') => makeRow(game, () => {
    if (query) recordRecent({ kind: 'query', label: query });
    recordRecent({ kind: 'game', label: game.title, payload: game });
  });
  const button = (label, className, action) => {
    const el = element('button', className, label);
    el.type = 'button';
    el.addEventListener('click', action);
    return el;
  };
  const recents = (list) => {
    const rows = getRecents().flatMap((recent) => {
      if (recent.kind === 'game' && recent.payload?.title && recent.payload.rom_filename
        && systemAvailable(recent.payload.system_id)) return [gameRow(recent.payload)];
      if (recent.kind !== 'query' || !recent.label.trim()) return [];
      const row = button('', 'rom-entry gc-row gc-row-query', () => {
        $('gc-rom-filter').value = recent.label;
        $('gc-rom-filter').focus();
        update();
      });
      row.dataset.title = recent.label;
      row.setAttribute('aria-label', `Search for ${recent.label}`);
      const mark = element('span', 'gc-query-mark', '⌕');
      mark.setAttribute('aria-hidden', 'true');
      row.append(mark, element('span', 'rom-entry-name', recent.label));
      return [row];
    });
    if (rows.length) group(list, 'Recent', rows);
    else list.append(element('p', 'gc-empty', 'Type a title to search every system.'));
    list.classList.toggle('gc-badges-none', !list.querySelector('.rp-badge-icon'));
    count('');
  };
  const render = (list, res, q) => {
    list.replaceChildren();
    const mode = deriveMode(res);
    const status = statusText({ mode, res, scopes: scopes() });
    // The compact field shows only the count; detailed scope status is visible below.
    const returned = res.games.items.length;
    const capped = mode === 'results' && returned < res.games.total;
    count(capped ? `${returned} of ${res.games.total}` : mode === 'results' ? status.split(' · ')[0] : '',
      capped ? `Showing ${returned} of ${res.games.total} games. Refine your search to see more.` : null);
    if (mode === 'results') {
      if (res.games.hidden_by_scope > 0) {
        const n = res.games.hidden_by_scope;
        const note = element('p', 'gc-scope-note', `${n} more game${n === 1 ? ' matches' : 's match'} without the filter. `);
        note.append(button('Show all', 'gc-link', resetScopes));
        list.append(note);
      }
      group(list, '', res.games.items.map((g) => gameRow(g, q)));
    } else if (mode === 'scope-zero') {
      const callout = element('div', 'gc-callout');
      callout.append(element('p', '', status));
      const actions = element('div', 'gc-callout-actions');
      actions.append(button('Turn off filter', 'gc-btn gc-btn-primary', resetScopes),
        button('Keep it on', 'gc-btn', () => callout.remove()));
      callout.append(actions);
      list.append(callout);
      group(list, 'Without the filter', (res.games.hidden_preview || []).map((g) => gameRow(g, q)));
    } else {
      list.append(element('p', 'gc-empty', `No game called "${q}".`));
      if (res.closest) group(list, 'Closest match', [gameRow(res.closest, q)]);
    }
    list.classList.toggle('gc-badges-none', !list.querySelector('.rp-badge-icon'));
    list.dataset.scope = activeScopeText(scopes());
  };
  const update = () => {
    cancel();
    const list = $('gc-search-list');
    if (!list || !current()) return;
    list.replaceChildren();
    list.scrollTop = 0;
    $('gc-no-match')?.classList.add('hidden');
    const q = ($('gc-rom-filter')?.value || '').trim();
    if (q.length < 2) { recents(list); return; }
    count('Searching…');
    const gen = generation;
    const owner = current();
    const owned = () => generation === gen && current() === owner;
    const s = scopes();
    timer = setTimeout(async () => {
      controller = new AbortController();
      const params = new URLSearchParams({ q, scope: 'games', limit: '100' });
      if (s.ach) params.set('ach', '1');
      if (s.score) params.set('score', '1');
      try {
        const response = await fetch(`/api/search?${params}`, { signal: controller.signal, credentials: 'include' });
        if (!owned()) return;
        if (!response.ok) throw new Error('search');
        const res = await response.json();
        if (!owned()) return;
        if (!Array.isArray(res?.games?.items) || res.games.unavailable) throw new Error('catalog');
        render(list, res, q);
      } catch {
        if (!owned()) return;
        list.replaceChildren(element('p', 'gc-empty', "Search isn't responding. Try again in a moment."),
          button('Try again', 'gc-retry', update));
        count('');
      }
    }, 250);
  };
  return { update, cancel };
}
